import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LoginRequestDTO } from "./dtos/login-request.dto";
import { map, tap } from "rxjs";
import { ApiResponse } from "shared/interfaces/response";
import { LoginResponseDTO } from "./dtos/login-response.dto";
import { StorageService } from "core/services/storage.service";
import { RegisterRequestDTO } from "./dtos/register-request.dto";
import { RouterService } from "core/services/router.service";
import { VerifyOtpRequestDTO } from "./dtos/verify-otp-request.dto";
import { RegisterResponseDTO } from "./dtos/register-response.dto";
import { ResendOtpRequestDTO } from "./dtos/resend-otp-request.dto";
import { User } from "shared/models/users/user";
import { ForgetPasswordRequestDTO } from "./dtos/forget-password-request.dto";
import { ForgetPasswordResponseDTO } from "./dtos/forget-password-response.dto";
import { ResetPasswordRequestDTO } from "./dtos/reset-password-request.dto";
import { ResetPasswordResponseDTO } from "./dtos/reset-password-response.dto";
import { ChangePasswordRequestDTO } from "./dtos/change-password-request.dto";
import { VerifyOtpResponseDTO } from "./dtos/verify-otp-response.dto";
import { RegisterFCMTokenRequestDTO } from "./dtos/register-fcm-token-request.dto";
import { RegisterFCMTokenResponseDTO } from "./dtos/register-fcm-token-response.dto";
import { SKIP_GLOBAL_LOADER } from "core/tokens/http-context.token";

@Injectable({
    providedIn: 'root'
})

export class AuthService {
    constructor(
        private http: HttpClient,
        private storageService: StorageService,
        private routerService: RouterService
    ) { }

    login(body: LoginRequestDTO) {
        return this.http.post<ApiResponse<LoginResponseDTO>>(`auth/ambassador/login`, body).pipe(
            map(resp => ({...resp, data: new LoginResponseDTO(resp.data)}))
        )
    }

    register(body: RegisterRequestDTO) {
        delete (body as any).confirmPassword;
        return this.http.post<ApiResponse<RegisterResponseDTO>>(`ambassadors/signup`, body).pipe(
            map((resp) => ({ ...resp, data: new RegisterResponseDTO(resp.data) }))
        )
    }

    logout() {
        return this.http.post(`auth/logout`, {}).pipe(
            tap(() => {
                this.storageService.clearStorage();
                this.routerService.goToLogin();
            })
        )
    }

    verifyOtp(body: VerifyOtpRequestDTO) {
        return this.http.post<ApiResponse<VerifyOtpResponseDTO>>(`ambassadors/verify-email`, body).pipe(
            map(resp => ({...resp, data: new VerifyOtpResponseDTO(resp.data)})),
            tap((resp) => this.setAuthData(resp.data))
        )
    }

    resendOtp(body: ResendOtpRequestDTO) {
        return this.http.post<ApiResponse<ResendOtpRequestDTO>>(`ambassadors/resend-otp`, body);
    }

    getMyProfile() {
        return this.http.get<ApiResponse<User>>(`ambassadors/me`).pipe(
            map((resp) => ({ ...resp, data: new User(resp.data) })),
            tap((resp) => this.storageService.user = resp.data)
        )
    }

    setAuthData(data: LoginResponseDTO) {
        const { accessToken, ambassador } = data;
        if (accessToken) this.storageService.accessToken = accessToken;
        if (ambassador) this.storageService.user = ambassador;
    }

    forgetPassword(body: ForgetPasswordRequestDTO) {
        return this.http.post<ApiResponse<ForgetPasswordResponseDTO>>(`ambassadors/forget-password`, body).pipe(
            map(resp => ({ ...resp, data: new ForgetPasswordResponseDTO(resp.data) }))
        )
    }

    resetPassword(body: ResetPasswordRequestDTO) {
        return this.http.post<ApiResponse<ResetPasswordResponseDTO>>(`ambassadors/reset-password`, body).pipe(
            map(resp => ({ ...resp, data: new ResetPasswordResponseDTO(resp.data) }))
        )
    }

    changePassword(body: ChangePasswordRequestDTO) {
        return this.http.post<ApiResponse>(`auth/change-password`, body)
    }

    registerFCMToken(body: RegisterFCMTokenRequestDTO) {
        const context = new HttpContext().set(SKIP_GLOBAL_LOADER, true);
        return this.http.post<ApiResponse<RegisterFCMTokenResponseDTO>>(`ambassadors/me/push-notifications/register`, body, { context }).pipe(
            map(resp => ({ ...resp, data: resp.data }))
        )
    }
}
